import React, { useRef } from 'react'
import { getSessionId } from '@fs/zion-session'
import { css } from '@emotion/core'
import { Grid, Cell, PersonBlock, HeaderBlock, useAtSize, WhiteText, Button, useStatusOverlay } from '@fs/zion-ui'
import { ContentDownload } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import html2canvas from 'html2canvas'
import TextRibbon from '../../../TextRibbon/TextRibbon'
import ProfileImage from '../../../ProfileImage/ProfileImage'
import { useAncestorImageData } from '../../../../../wcf/global/hooks'
import { PersonType } from '../../../../propTypes/personDataProps'
import getRelation from '../../../../../wcf/global/findRelation'
import getDisplayName from '../../../../helpers/getDisplayName'
import PersonInfoLifespan from './PersonInfoLifespan'
import vitalStrings from '../../../../helpers/vitalStrings'
import { getGender } from '../../../../helpers/person_Helper'
import useDSApplicationMode from '../../../../hooks/useDSApplicationMode'
import RelativeTakeaway from '../../../../../wcf/AppWcf/components/Heritage/takeaways/RelativeTakeaway'
import { useUploadTakeAway } from '../../../StationMenu/hooks'

const getZionStyle = (k) =>
  ({
    colorWhite: '#ffffff',
    colorBlack: '#000000',
    colorBlueGenderLight: '#7DE3F4',
    colorPinkGenderLight: '#FC4E6D',
    colorGrayGenderLight: '#ccccc4',
    colorBlueGender: '#99c4fe',
    colorPinkGender: '#f48eb6',
    colorGrayGender: '#ccccc4',
    fontSizeSmall: '14px',
  }[k] || '')

const getGenderColor = (g) =>
  ({
    male: getZionStyle('colorBlueGenderLight'),
    female: getZionStyle('colorPinkGenderLight'),
    unknown: getZionStyle('colorGrayGenderLight'),
  }[g] || getZionStyle('colorGrayGenderLight'))

const getGenderGradient = (g) =>
  ({
    male: 'linear-gradient(90deg, #003057 0%, #177c9c 100%)',
    female: 'linear-gradient(90deg, #8f124a 0%, #bd0057 100%);',
    unknown: 'linear-gradient(90deg, #aaaaaa 0%, #dddddd 100%);',
  }[g] || 'linear-gradient(90deg, #aaaaaa 0%, #dddddd 100%);')

const cssHeaderBox = (gender) => css`
  background: ${getGenderGradient(gender)};
`
const cssInfoBox = css`
  display: inline-block;
  color: ${getZionStyle('colorWhite')};
  margin: auto 20px;
  @media screen and (min-width: 320px) and (max-width: 823px) and (orientation: landscape) {
    margin: auto 0;
  }
`

const cssInfoRibbon = css`
  color: ${getZionStyle('colorWhite')};
`

const cssRelation = css`
  display: block;
  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;
  @media screen and (min-width: 320px) and (max-width: 823px) and (orientation: landscape) {
    font-weight: normal;
  }
`
const gridCSS = css`
  width: 100%;
  color: #fff;
  padding: 20px;
  @media screen and (min-width: 320px) and (max-width: 823px) and (orientation: landscape) {
    padding: 8px;
  }
`
const selfAlignerCSS = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`
export default function PersonHeader({ personData }) {
  const atSize = useAtSize()
  const sessionId = getSessionId()
  const userImage = useAncestorImageData({ personId: personData.personId, sessionId })
  const displayName = getDisplayName(personData)
  const vitalstrings = vitalStrings(personData) ? vitalStrings(personData) : null
  const [t] = useTranslation()
  const appMode = useDSApplicationMode()
  const relativeTakeaway = useRef()
  const [uploadState, uploadFunc] = useUploadTakeAway()
  const showStatusOverlay = useStatusOverlay()
  const azUSID = sessionStorage.getItem('userSessionId')

  const nameCss = css`
    display: flex;
    align-items: center;
  `

  const takeawayButtonCss = css`
    margin-left: 20px;
  `

  const takeawayWrapperCss = css`
    position: fixed;
    width: 1px;
    height: 1px;
    top: 0px;
    left: 0px;
    overflow: hidden;
    > div {
      width: 750px;
    }
  `

  const person = {
    id: personData.personId,
    atn: personData.atn,
    name: displayName || 'Unknown',
    gender: getGender(personData.gender),
    relation: personData.relation ?? getRelation(personData),
    lifespan: personData.lifespan,
  }

  const PersonInfoRelation = ({ ribbonType }) => {
    const props = {
      type: ribbonType,
      color: getZionStyle('colorBlack'),
      backgroundColor: getGenderColor(person.gender),
      size: '14px',
    }

    return (
      <div css={cssInfoRibbon}>
        <TextRibbon {...props}>
          <span css={cssRelation}>{person.relation}</span>
        </TextRibbon>
      </div>
    )
  }

  const triggerTakeAway = async () => {
    const canvas = await html2canvas(relativeTakeaway.current, { useCORS: true })
    const imgDataURL = canvas.toDataURL('png')
    uploadFunc({ imgDataURL, azUSID })
  }

  React.useEffect(() => {
    if (uploadState.pending) {
      showStatusOverlay({
        message: t('station-kiosk.uploading', 'Uploading...'),
      })
    } else if (uploadState.complete && uploadState.error) {
      showStatusOverlay({
        message: t('station-kiosk.uploading-failed', 'Something went wrong!'),
        type: 'error',
      })
    } else if (uploadState.complete) {
      showStatusOverlay({
        message: t('station-kiosk.uploading-complete', 'Upload complete!'),
        type: 'success',
      })
    }
  }, [showStatusOverlay, t, uploadState.complete, uploadState.error, uploadState.pending])
  return (
    <div css={cssHeaderBox(person.gender)}>
      <div css={gridCSS}>
        <WhiteText>
          <Grid>
            <Cell columns={atSize({ default: 12, sm: 'hide' })}>
              <Cell>
                <Cell columns={atSize({ default: 12 })}>
                  <div css={selfAlignerCSS}>
                    <PersonBlock
                      avatarProps={{ src: userImage, sex: person.gender }}
                      centered
                      name={person.name}
                      size="lg"
                    />
                  </div>
                </Cell>
                <Cell columns={atSize({ default: 12 })}>
                  <div css={selfAlignerCSS}>
                    <PersonInfoRelation ribbonType="ribbon-ribbon" />
                  </div>
                </Cell>
                <Cell columns={atSize({ default: 12 })}>
                  <div css={selfAlignerCSS}>
                    <HeaderBlock centered heading={vitalstrings.lifespan} size="xs" />
                  </div>
                </Cell>
              </Cell>
            </Cell>
            <Cell columns={atSize({ default: 'hide', sm: 12 })} key="test">
              <Grid>
                {/* would be helpful if landscape and sm to be 2 instead of 3 V */}
                <Cell columns={atSize({ sm: 3, xl: 2 })}>
                  <ProfileImage
                    gender={person.gender}
                    userImage={userImage}
                    name={person.name}
                    border="6px solid #FFF"
                  />
                </Cell>
                <Cell columns={atSize({ sm: 9, xl: 10 })}>
                  <div css={cssInfoBox}>
                    <PersonInfoRelation ribbonType="none-ribbon" />
                    <div css={nameCss}>
                      <HeaderBlock heading={person.name} size="lg" />
                      {appMode === 'STATION' ||
                        (appMode === 'KIOSK' && (
                          <div css={takeawayButtonCss}>
                            <Button
                              Icon={ContentDownload}
                              type="button"
                              onClick={triggerTakeAway}
                              emphasis="billboard"
                              color={personData.gender === 'male' ? 'blue' : 'red'}
                            >
                              {t('wcf.heritage.food.save-relative', 'Save Relative')}
                            </Button>
                          </div>
                        ))}
                    </div>
                    <PersonInfoLifespan vitalStrings={vitalstrings} {...personData} />
                  </div>
                </Cell>
              </Grid>
            </Cell>
          </Grid>
        </WhiteText>
      </div>
      <div css={takeawayWrapperCss}>
        <div ref={relativeTakeaway}>
          <RelativeTakeaway
            profile={userImage}
            relation={person.relation}
            heading={`${personData.name} ${personData.surname}`}
            subHeading={vitalstrings.lifespan}
            gender={personData.gender}
          />
        </div>
      </div>
    </div>
  )
}

PersonHeader.propTypes = {
  personData: PersonType,
}
